import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  type APIFees, InvalidPaymentRequest, type SendPlan, parseAmount, parsePaymentRequest, plainAmount, tryPaymentRequest,
} from '../core';
import { formatCW, grouped } from '../app/format';
import { PlainButton, PrimaryButton, SecondaryButton, Sheet, Spinner, copyText, haptic } from './components';
import { ContactRow } from './contacts';
import { CheckCircle, Clipboard, Close, ContactIcon, Copy, Check, FaceID, QRScan } from './icons';
import { ui, useApp } from './state';

type Speed = 'slow' | 'normal' | 'fast';

export function SendSheet({ prefill }: { prefill?: string }) {
  const { s, model } = useApp();
  const [addressText, setAddressText] = useState('');
  const [amountText, setAmountText] = useState('');
  const [sendAll, setSendAll] = useState(false);
  const [subtractFee, setSubtractFee] = useState(false);
  const [label, setLabel] = useState<string | null>(null);
  const [fees, setFees] = useState<APIFees | null>(null);
  const [speed, setSpeed] = useState<Speed>('normal');
  const [scanError, setScanError] = useState<string | null>(null);
  const [review, setReview] = useState<SendPlan | null>(null);

  useEffect(() => {
    if (prefill) apply(prefill);
    model.fees().then(setFees).catch(() => {});
  }, []);

  const request = tryPaymentRequest(addressText);
  const contact = request ? model.contactFor(request.address.string) : undefined;
  const recipientName = contact?.name ?? label ?? null;
  const feeRate = fees ? fees[speed] : 1;
  const feesVary = (fees?.fast ?? 0) > (fees?.slow ?? 0);

  const addressProblem = useMemo(() => {
    const t = addressText.trim();
    if (!t) return null;
    try {
      parsePaymentRequest(t);
      return null;
    } catch (e) {
      const r = e instanceof InvalidPaymentRequest ? e.reason : 'invalidAddress';
      return r === 'invalidAmount' ? s.addressAmountInvalid : r === 'unsupportedRequirement' ? s.addressUnsupported : s.addressInvalid;
    }
  }, [addressText, s]);

  let plan: SendPlan | null = null;
  let planError: string | null = null;
  if (request) {
    const amount = sendAll ? null : parseAmount(amountText);
    if (sendAll || (amount !== null && amount > 0)) {
      try {
        plan = model.prepareSend(request.address, amount, feeRate, subtractFee && !sendAll);
      } catch (e) {
        planError = model.describe(e);
      }
    }
  }

  function apply(text: string) {
    setScanError(null);
    const t = text.trim();
    const r = tryPaymentRequest(t);
    if (!r) return setAddressText(t);
    setAddressText(r.address.string);
    setLabel(r.label);
    if (r.amount !== null) {
      setSendAll(false);
      setAmountText(plainAmount(r.amount));
    }
  }

  if (review) return <ReviewSend plan={review} recipientName={recipientName} onBack={() => setReview(null)} />;

  const available = model.snapshot?.coins.reduce((n, c) => n + c.value, 0) ?? 0;

  return (
    <Sheet title={s.sendTitle} left={<PlainButton onClick={() => ui.close()}>{s.cancel}</PlainButton>}>
      <section class="group">
        <div class="group-title">{s.to}</div>
        <div class="group-body">
          {contact ? (
            <div class="row">
              <ContactRow contact={contact} />
              <PlainButton aria-label={s.clear} onClick={() => { setAddressText(''); setLabel(null); }}><Close size={18} /></PlainButton>
            </div>
          ) : (
            <div class="row input-row">
              <textarea rows={1} class="mono address-input" placeholder={s.addressPlaceholder} value={addressText}
                autocapitalize="off" autocomplete="off" spellcheck={false}
                onInput={(e) => {
                  const v = (e.target as HTMLTextAreaElement).value;
                  // A pasted/typed coinwow: code or app.coinwow.ca/pay link fills the address and the amount.
                  if (/^(coinwow:|https:\/\/app\.coinwow\.ca\/pay)/i.test(v.trim()) && tryPaymentRequest(v)) apply(v);
                  else { setAddressText(v); setScanError(null); }
                }} />
              {addressText
                ? <PlainButton aria-label={s.clear} onClick={() => { setAddressText(''); setLabel(null); }}><Close size={18} /></PlainButton>
                : <PlainButton aria-label={s.paste} onClick={async () => { try { apply(await navigator.clipboard.readText()); } catch {} }}><Clipboard size={20} /></PlainButton>}
              <PlainButton aria-label={s.contactsTitle} onClick={() => ui.open({ type: 'contacts', pick: (c) => { setLabel(null); setAddressText(c.address); } })}><ContactIcon size={20} /></PlainButton>
              <PlainButton aria-label={s.scanQR} onClick={() => ui.open({ type: 'scanner', onCode: (c) => (tryPaymentRequest(c) ? apply(c) : setScanError(s.scanNotCoinwow)) })}><QRScan size={20} /></PlainButton>
            </div>
          )}
        </div>
        <div class="group-footer">
          {scanError ?? addressProblem ? <span class="error">{scanError ?? addressProblem}</span>
            : request && model.isOwnAddress(request.address) ? s.ownAddressWarning
            : !contact && label ? label : null}
        </div>
      </section>

      <section class="group">
        <div class="group-title">{s.amount}</div>
        <div class="group-body">
          <div class="row amount-row">
            {sendAll
              ? <button type="button" class="plain amount-input static" onClick={() => setSendAll(false)}>{plan ? plainAmount(plan.amount) : '—'}</button>
              : <input class="amount-input" inputMode="decimal" placeholder="0" value={amountText}
                  onInput={(e) => setAmountText((e.target as HTMLInputElement).value)} />}
            <span class="dim unit">CW</span>
            <button type="button" class={`max-pill ${sendAll ? 'on' : ''}`} onClick={() => { setSendAll(!sendAll); if (!sendAll) setAmountText(''); }}>
              {sendAll ? 'Max ✓' : 'Max'}
            </button>
          </div>
        </div>
        <div class="group-footer">
          {planError ? <span class="error">{planError}</span>
            : amountText && !sendAll && parseAmount(amountText) === null ? <span class="error">{s.amountInvalid}</span>
            : model.snapshot ? s.available(formatCW(available)) : null}
        </div>
      </section>

      <section class="group">
        <div class="group-title">{s.fees}</div>
        {feesVary && (
          <div class="segmented" role="radiogroup" aria-label={s.speed}>
            {(['slow', 'normal', 'fast'] as Speed[]).map((sp) => (
              <button type="button" role="radio" aria-checked={speed === sp} class={speed === sp ? 'on' : ''} onClick={() => setSpeed(sp)}>
                {sp === 'slow' ? s.feeSlow : sp === 'normal' ? s.feeNormal : s.feeFast}
              </button>
            ))}
          </div>
        )}
        <div class="group-body">
          <div class="row"><span class="row-label">{s.networkFee}</span><span class="row-value tabular">{plan ? formatCW(plan.fee) : '—'}</span></div>
          {!sendAll && (
            <label class="row">
              <span class="row-label">{s.subtractFee}</span>
              <input type="checkbox" checked={subtractFee} onChange={(e) => setSubtractFee((e.target as HTMLInputElement).checked)} />
            </label>
          )}
        </div>
        <div class="group-footer">{s.feesFooter}</div>
      </section>

      <PrimaryButton disabled={!plan} onClick={() => plan && setReview(plan)}>{s.reviewSend}</PrimaryButton>
    </Sheet>
  );
}

function ReviewSend({ plan, recipientName, onBack }: { plan: SendPlan; recipientName: string | null; onBack: () => void }) {
  const { s, model } = useApp();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txid, setTxid] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const known = model.contactFor(plan.recipient.string);

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const id = await model.send(plan);
      if (id) {
        haptic();
        setTxid(id);
      }
    } catch (e) {
      haptic();
      setError(model.describe(e));
    }
    setSending(false);
  };

  if (txid) {
    return (
      <Sheet>
        <div class="stack-20 grow text-center center-col">
          <div class="spacer" />
          <CheckCircle size={72} strokeWidth={1.6} />
          <h1 class="large-title">{s.sentTitle}</h1>
          <p class="dim">{s.sentText(formatCW(plan.amount))}</p>
          <PlainButton class="dim small" onClick={async () => { await copyText(txid).catch(() => {}); setCopied(true); }}>
            {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? s.txidCopied : s.copyTxid}
          </PlainButton>
          <div class="spacer" />
          {!known && (
            <SecondaryButton onClick={() => ui.open({ type: 'contactEdit', contact: { id: crypto.randomUUID(), name: recipientName ?? '', address: plan.recipient.string } })}>
              {s.addToContacts}
            </SecondaryButton>
          )}
          <PrimaryButton onClick={() => ui.closeAll()}>{s.done}</PrimaryButton>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet title={s.review} left={sending ? undefined : <PlainButton aria-label={s.sendTitle} onClick={onBack}>‹</PlainButton>}>
      <div class="center-col stack-6 review-hero">
        <span class="dim">{s.youSend}</span>
        <span class="amount-big">{formatCW(plan.amount)}</span>
      </div>
      <div class="card">
        <div class="review-row">
          <span class="dim">{s.to}</span>
          <span class="text-right">
            {recipientName && <span class="strong block">{recipientName}</span>}
            <span class="mono small">{grouped(plan.recipient.string)}</span>
          </span>
        </div>
        <div class="divider" />
        <div class="review-row"><span class="dim">{s.networkFee}</span><span class="tabular">{formatCW(plan.fee)}</span></div>
        <div class="divider" />
        <div class="review-row"><span class="dim">{s.totalDebited}</span><span class="strong tabular">{formatCW(plan.amount + plan.fee)}</span></div>
      </div>
      <p class="text-center small warning-text">{s.irreversibleWarning}</p>
      <div class="spacer" />
      {error && <p class="error text-center">{error}</p>}
      <PrimaryButton disabled={sending} onClick={send}>{sending ? <Spinner /> : <><FaceID size={20} /> {s.confirmSend}</>}</PrimaryButton>
    </Sheet>
  );
}
